<template>
  <section v-if="enrichedStays.length > 0" class="relative overflow-hidden" data-testid="experiential-stays-section">
    <!-- 背景: PC=アクティブ体験の1枚目、SP=先頭体験の1枚目（300msクロスフェード） -->
    <div class="absolute inset-0">
      <Transition mode="out-in" name="stay-fade">
        <OptimizedImage
          :key="activeStay.slug"
          alt=""
          aria-hidden="true"
          class="absolute inset-0 h-full w-full object-cover"
          fetchpriority="low"
          loading="lazy"
          :image-url="backgroundImageUrl"
          :optimized-image-options="{ width: 1440 }"
        />
      </Transition>
      <div class="absolute inset-0 bg-black/80"></div>
    </div>

    <!-- SP レイアウト: 横スクロールカードカルーセル -->
    <div class="relative flex flex-col gap-4 px-4 py-20 md:hidden">
      <nuxt-link class="flex items-center gap-2 hover:opacity-80" :to="CURATED_STAYS_VIEW_MORE_URL">
        <span class="font-Spectral text-2xl font-medium capitalize leading-8 tracking-[0.5px] text-gold4">Experiential Stays</span>
        <span class="material-symbols-sharp text-2xl leading-none text-gold4">keyboard_arrow_right</span>
        <span class="h-px flex-1 bg-gold4/60"></span>
      </nuxt-link>

      <!-- scroll-pl-4: snap-start で消える 16px インデントを維持。高さはカード内容で可変（Figma の h-[511px] はサンプル値で本番データのメタ折り返しで超過しやすいため固定化しない） -->
      <div class="slide-scrollbar -mx-4 flex snap-x snap-mandatory scroll-pl-4 items-start gap-2 overflow-x-auto px-4 pb-1">
        <article v-for="stay in enrichedStays" :key="stay.slug" class="flex shrink-0 snap-start flex-col gap-2">
          <nuxt-link class="block h-[375px] w-[300px] overflow-hidden" :data-testid="`experiential-stay-card-sp-${stay.slug}`" :to="stay.detailLink" @click="handleCardClick(stay)">
            <OptimizedImage class="h-full w-full object-cover" loading="lazy" :alt="stay.titleEn" :image-url="stay.cardImageUrl" :optimized-image-options="{ width: 600 }" />
          </nuxt-link>
          <div class="flex w-[300px] flex-col gap-1">
            <p class="line-clamp-3 font-Spectral text-base leading-6 text-white">
              {{ stay.titleEn }}
            </p>
            <p v-if="stay.meta.hasMeta" class="flex flex-wrap items-center gap-x-1 font-Spectral text-sm leading-5">
              <span v-if="stay.meta.destination" class="text-[#f4f1ed]">{{ stay.meta.destination }}</span>
              <span v-if="stay.meta.destination && (stay.meta.duration || stay.meta.price)" aria-hidden="true" class="text-[#f4f1ed]">|</span>
              <template v-if="stay.meta.duration">
                <span class="material-symbols-sharp text-base leading-none text-[#f4f1ed]">schedule</span>
                <span class="text-[#f4f1ed]">{{ stay.meta.duration }}</span>
              </template>
              <span v-if="stay.meta.duration && stay.meta.price" aria-hidden="true" class="text-[#a7a199]">|</span>
              <span v-if="stay.meta.price" class="text-[#f4f1ed]">{{ stay.meta.price }}</span>
            </p>
          </div>
          <nuxt-link class="flex items-center justify-end gap-1 font-Spectral text-sm leading-5 text-gold4 hover:opacity-80" :to="stay.detailLink" @click="handleCardClick(stay)">
            <span>View more</span>
            <span class="material-symbols-sharp text-2xl leading-none">chevron_right</span>
          </nuxt-link>
        </article>
      </div>

      <nuxt-link
        class="mx-auto flex h-12 w-full min-w-[120px] max-w-[368px] items-center justify-center border border-gold4 px-6 font-Spectral text-[17px] leading-6 text-gold4 hover:opacity-80"
        :to="CURATED_STAYS_VIEW_MORE_URL"
      >
        View all Experiential stays
      </nuxt-link>
    </div>

    <!-- PC レイアウト: 単一フォーカス + 矢印切替 + 自動スライドショー -->
    <div class="relative mx-auto hidden max-w-[1440px] flex-col items-center gap-8 px-[112px] py-20 md:flex">
      <div class="relative flex w-full flex-row items-end gap-14">
        <!-- 左: テキストパネル（画像レイヤーより前面に重ねる）。h-[700px] で画像と同じ高さを確保し、矢印を下端固定 / その他コンテンツを縦中央寄せにする -->
        <div class="relative z-10 flex h-[700px] w-[600px] flex-col items-center pt-4">
          <!-- 上スペーサー: 残り高さを下スペーサーと等分して中央コンテンツを縦中央に寄せる -->
          <div class="flex-1"></div>

          <nuxt-link class="mb-4 flex items-center gap-1 font-Spectral font-medium capitalize tracking-[0.5px] text-gold4 hover:opacity-80" :to="CURATED_STAYS_VIEW_MORE_URL">
            <span class="text-[30px] leading-[40px]">Experiential Stays</span>
            <span class="material-symbols-sharp text-2xl leading-none">keyboard_arrow_right</span>
          </nuxt-link>
          <div class="h-px w-full bg-gold4"></div>

          <Transition mode="out-in" name="stay-fade">
            <!-- min-h は本番最長のタイトル 4 行 + meta を収める下限。理論値(144+8+20=172px)だと実レンダリングの
                 行高差で 3 行↔4 行の切替時に数 px ジャンプするため、余裕を見て 180px に設定して縦位置を固定する。
                 my-8(32px) が「上の線→タイトル」「メタ→View details」の基準間隔（Figma 準拠、3 行未満時は中央寄せで均等に余る）。 -->
            <div :key="activeStay.slug" class="my-8 flex min-h-[180px] w-full flex-col items-center justify-center gap-8 px-1">
              <div class="flex w-full flex-col gap-2">
                <h3 class="font-Spectral text-3xl font-medium capitalize leading-9 tracking-[0.5px] text-white">
                  {{ activeStay.titleEn }}
                </h3>
                <p v-if="activeStay.meta.hasMeta" class="flex flex-wrap items-center gap-x-1 font-Spectral text-sm leading-5">
                  <span v-if="activeStay.meta.destination" class="text-[#f4f1ed]">{{ activeStay.meta.destination }}</span>
                  <span v-if="activeStay.meta.destination && (activeStay.meta.duration || activeStay.meta.price)" aria-hidden="true" class="text-[#f4f1ed]">|</span>
                  <template v-if="activeStay.meta.duration">
                    <span class="material-symbols-sharp text-base leading-none text-[#f4f1ed]">schedule</span>
                    <span class="text-[#f4f1ed]">{{ activeStay.meta.duration }}</span>
                  </template>
                  <span v-if="activeStay.meta.duration && activeStay.meta.price" aria-hidden="true" class="text-[#a7a199]">|</span>
                  <span v-if="activeStay.meta.price" class="text-[#f4f1ed]">{{ activeStay.meta.price }}</span>
                </p>
              </div>
            </div>
          </Transition>

          <!-- View details は「アクション」であり体験ごとに見た目が変わらないため、フェード対象外として固定。リンク先と data-testid だけが切替時に即時更新される -->
          <nuxt-link
            class="mb-8 flex h-12 w-full min-w-[120px] max-w-[368px] items-center justify-center bg-dark-bg-accent px-6 font-Spectral text-[17px] leading-6 text-gray9 hover:opacity-90"
            :data-testid="`experiential-stay-card-${activeStay.slug}`"
            :to="activeStay.detailLink"
            @click="handleViewDetailsClick"
          >
            View details
          </nuxt-link>

          <div class="h-px w-full bg-gold4"></div>

          <!-- 下スペーサー: 上スペーサーと等分して中央コンテンツを縦中央に寄せる -->
          <div class="flex-1"></div>

          <!-- 矢印: カード下端 + 40px の位置に固定（Figma 準拠） -->
          <div class="mb-10 flex items-center gap-4">
            <button
              aria-label="Previous stay"
              class="flex h-10 w-10 items-center justify-center border transition-opacity"
              type="button"
              :class="canGoPrev ? 'border-gold4 text-gold4 hover:opacity-80' : 'cursor-not-allowed border-[#777] text-[#909090]'"
              :disabled="!canGoPrev"
              @click="goPrev"
            >
              <span class="material-symbols-sharp text-3xl leading-none">keyboard_arrow_left</span>
            </button>
            <button
              aria-label="Next stay"
              class="flex h-10 w-10 items-center justify-center border transition-opacity"
              type="button"
              :class="canGoNext ? 'border-gold4 text-gold4 hover:opacity-80' : 'cursor-not-allowed border-[#777] text-[#909090]'"
              :disabled="!canGoNext"
              @click="goNext"
            >
              <span class="material-symbols-sharp text-3xl leading-none">keyboard_arrow_right</span>
            </button>
          </div>
        </div>

        <!-- 右: コンテンツ写真エリア。各体験画像を絶対配置し、prev は説明カード左、current は右に並ぶ位置、next は右側にはみ出す位置にスロット配置する -->
        <!-- clip-path で左側のみクリップ（next の右側はみ出し peek は維持）。prev 画像がテキストパネル左にはみ出すのを防ぐ -->
        <div class="relative h-[700px] w-[560px] [clip-path:inset(0_-100vw_0_0)]">
          <div v-for="(it, i) in enrichedStays" :key="it.slug" class="stay-image-slot absolute inset-y-0 left-0 h-full w-[560px]" :style="imageSlotStyles[i]">
            <!-- アクティブ体験のみ自動スライドショー。非アクティブ（隣にはみ出す画像）は1枚目固定で意図しない切替を防ぐ -->
            <ImageMediaSlider
              v-if="i === activeIndex && it.slideshowImages.length > 0"
              :pc-optimized-image-options="{ width: 1120 }"
              :slides="it.slideshowImages"
              :sp-optimized-image-options="{ width: 1120 }"
            />
            <OptimizedImage v-else class="h-full w-full object-cover" loading="lazy" :alt="it.titleEn" :image-url="it.staticImage" :optimized-image-options="{ width: 1120 }" />
            <!-- アクティブ体験の画像クリックで詳細ページに遷移する。透明オーバーレイで ImageMediaSlider の表示を阻害しない -->
            <nuxt-link v-if="i === activeIndex" class="absolute inset-0 z-10" :aria-label="`View details of ${it.titleEn}`" :to="it.detailLink" @click="handleViewDetailsClick" />
            <!-- prev / next の画像はグレーのオーバーレイで彩度を落とす（Figma 準拠） -->
            <div v-if="i !== activeIndex" aria-hidden="true" class="pointer-events-none absolute inset-0 bg-black/50"></div>
          </div>
        </div>
      </div>

      <nuxt-link
        class="flex h-12 w-full min-w-[120px] max-w-[368px] items-center justify-center border border-gold4 px-6 font-Spectral text-[17px] leading-6 text-gold4 hover:opacity-80"
        :to="CURATED_STAYS_VIEW_MORE_URL"
      >
        View all Experiential stays
      </nuxt-link>
    </div>
  </section>
</template>

<script lang="ts" setup>
import OptimizedImage from '@/wabunka-src/common/components/stateful/OptimizedImage/OptimizedImage.vue';
import ImageMediaSlider from '@/wabunka-src/common/features/Featured/components/ImageMediaSlider/ImageMediaSlider.vue';
import { CURATED_STAYS_VIEW_MORE_URL } from '@/wabunka-src/constants/navigation';
import type { ExperientialStayCardListViewModel, ExperientialStayCardViewModel } from '@/wabunka-src/features/Top/models/experientialStay';

interface Props {
  experientialStayList: ExperientialStayCardListViewModel;
  onFeaturedClick?: (type: string, id: string) => void;
}

const props = defineProps<Props>();

// 右画像エリア（PC）のレイアウト定数
// スロットの基準位置は「説明カード右、56px ギャップを挟んだ画像エリアの左端」（translateX(0)）
const SLIDE_WIDTH_PX = 560;
// 説明カードと画像エリアの gap。current と説明カードの間、prev と説明カードの間の両方で同じ余白を取る
const TEXT_PANEL_GAP_PX = 56;
// 説明カード幅 (600) + 説明カード右側の gap (56) = 656。基準位置 translateX(0) から見た説明カード左端までの距離
const TEXT_PANEL_OFFSET_PX = 656;
// current と next のあいだの隙間（Figma の 24px に揃える）
const NEXT_GAP_PX = 24;
const PREV_SLOT_X = -(TEXT_PANEL_OFFSET_PX + TEXT_PANEL_GAP_PX + SLIDE_WIDTH_PX);
const NEXT_SLOT_X = SLIDE_WIDTH_PX + NEXT_GAP_PX;

const activeIndex = ref(0);

// 表示用に整形したカードリスト。meta / detailLink はカード生成時（useExperientialStays）に確定済みのため、
// ここでは画像 URL とスライドショー画像のみ事前計算する（テンプレート内での関数呼び出しを避ける）
const enrichedStays = computed(() =>
  props.experientialStayList.map((card) => ({
    ...card,
    // SP カードは縦長画像、なければ横長画像にフォールバック
    cardImageUrl: card.backgroundTallMedias?.[0]?.url ?? card.backgroundWideMedias?.[0]?.url ?? '',
    // PC スライドショー（アクティブカードのみ利用）。ImageMediaSlider は SP/PC 別 URL の slides を受け取るが、
    // 横長メディアは SP/PC 共通のため同一 URL を sp/pc 双方に割り当てる。
    slideshowImages: card.backgroundWideMedias?.map((m) => ({ sp: m.url, pc: m.url })) ?? [],
    // 非アクティブスロット用の静止画像（横長画像 1 枚目を優先、なければ縦長画像にフォールバック）
    staticImage: card.backgroundWideMedias?.[0]?.url ?? card.backgroundTallMedias?.[0]?.url ?? '',
  })),
);

// experientialStayList の参照が変わったとき、現在アクティブなカードの slug をベースに index を追従させる。
// 同じ slug が新リストにも含まれていればその位置に移動し、含まれなければ先頭にリセットする
watch(
  () => props.experientialStayList,
  (newList, oldList) => {
    if (!oldList) return;
    const currentSlug = oldList[activeIndex.value]?.slug;
    const newIndex = currentSlug ? newList.findIndex((card) => card.slug === currentSlug) : -1;
    activeIndex.value = newIndex >= 0 ? newIndex : 0;
  },
);

// PC: アクティブカード
const activeStay = computed(() => enrichedStays.value[activeIndex.value] ?? enrichedStays.value[0]);

const backgroundImageUrl = computed(() => activeStay.value?.backgroundWideMedias?.[0]?.url ?? activeStay.value?.backgroundTallMedias?.[0]?.url ?? '');

// 各画像 slot の表示位置を「activeIndex からの相対距離 d」に応じて算出する
// d = 0: current（説明カード右、画像エリアの基準位置 translateX(0)）
// d = -1: prev（説明カードの左側に 56px ギャップを挟んで隣接。section の左パディング内まではみ出す）
// d = +1: next（current の右、24px ギャップ）
// |d| >= 2: 画面外（さらに同方向にずらして積み重ねる）
const getImageSlotStyle = (i: number) => {
  const d = i - activeIndex.value;
  let x: number;
  if (d === 0) x = 0;
  else if (d === -1) x = PREV_SLOT_X;
  else if (d === 1) x = NEXT_SLOT_X;
  else if (d < -1) x = PREV_SLOT_X + (d + 1) * (SLIDE_WIDTH_PX + NEXT_GAP_PX);
  else x = NEXT_SLOT_X + (d - 1) * (SLIDE_WIDTH_PX + NEXT_GAP_PX);
  return { transform: `translateX(${x}px)` };
};

// 各画像 slot のスタイルを事前計算（テンプレートからの関数呼び出しを避け、再レンダリング時の再生成を防ぐ）
const imageSlotStyles = computed(() => enrichedStays.value.map((_, i) => getImageSlotStyle(i)));

const canGoPrev = computed(() => activeIndex.value > 0);
const canGoNext = computed(() => activeIndex.value < props.experientialStayList.length - 1);

const goPrev = () => {
  if (canGoPrev.value) {
    activeIndex.value -= 1;
  }
};

const goNext = () => {
  if (canGoNext.value) {
    activeIndex.value += 1;
  }
};

// GA の section_id。トップの本セクションは旧 Itineraries 特集ではなく Experiential Stays（Curated_Stays）に
// なったため、本物の旅程特集が使う 'featured-itinerary' とは別キー 'featured-experiential-stay' で計測する。
const GA_SECTION_ID = 'featured-experiential-stay';

const handleViewDetailsClick = () => {
  if (props.onFeaturedClick && activeStay.value) {
    props.onFeaturedClick(GA_SECTION_ID, activeStay.value.slug);
  }
};

const handleCardClick = (card: ExperientialStayCardViewModel) => {
  props.onFeaturedClick?.(GA_SECTION_ID, card.slug);
};
</script>

<style lang="scss" scoped>
// 体験切り替え時のクロスフェード（背景画像 / PC テキストパネル用）。
// Vue の <Transition mode="out-in"> は leave 完了後に enter が始まるため、
// 仕様の「約 300ms」に合わせて leave / enter をそれぞれ 150ms にして合計 300ms に揃える
.stay-fade-enter-active,
.stay-fade-leave-active {
  transition: opacity 0.15s ease;
}

.stay-fade-enter-from,
.stay-fade-leave-to {
  opacity: 0;
}

// 体験切り替え時のスライド（300ms）。各画像 slot が個別に translateX をアニメーションする
.stay-image-slot {
  transition: transform 0.3s ease;
  will-change: transform;
}
</style>
